import React, { type ReactNode } from 'react';

// Small presentational pieces shared across screens. Styling lives in
// theme/global.css so these stay markup-only.
//
// A subset of busLineSrv's ui.tsx: the table/stat/date helpers it also carries
// exist for admin data grids, and there is no data grid on a public site.

export const Spinner: React.FC<{ label?: string }> = ({ label }) => (
    <div style={{ padding: 24, textAlign: 'center' }}>
        <div className="spinner" role="status" aria-label={label ?? 'Loading'} />
    </div>
);

type NoticeKind = 'error' | 'warning' | 'success' | 'info';

export const Notice: React.FC<{ kind: NoticeKind; children: ReactNode }> = ({ kind, children }) => (
    <div className={`notice notice--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
        {children}
    </div>
);

export const Field: React.FC<{
    label: string;
    htmlFor: string;
    children: ReactNode;
}> = ({ label, htmlFor, children }) => (
    <div className="field">
        <label className="field__label" htmlFor={htmlFor}>{label}</label>
        {children}
    </div>
);

/** A section of the landing page: consistent vertical rhythm and heading block. */
export const Section: React.FC<{
    id?: string;
    tint?: boolean;
    eyebrow?: string;
    title?: string;
    sub?: string;
    children: ReactNode;
}> = ({ id, tint, eyebrow, title, sub, children }) => (
    <section id={id} className={`section${tint ? ' section--tint' : ''}`}>
        <div className="wrap">
            {title ? (
                <header className="section__head">
                    {eyebrow ? <div className="section__eyebrow">{eyebrow}</div> : null}
                    <h2 className="section__title">{title}</h2>
                    {sub ? <p className="section__sub">{sub}</p> : null}
                </header>
            ) : null}
            {children}
        </div>
    </section>
);

/**
 * Stand-in for artwork that does not exist yet. Renders a labelled dashed box
 * rather than a stock image on purpose — a placeholder that looks like a
 * placeholder gets replaced; one that looks finished ships.
 */
export const Placeholder: React.FC<{ shape: 'phone' | 'wide'; label: string; hint?: string }> = ({
    shape, label, hint,
}) => (
    <div className={`shot shot--${shape}`} role="img" aria-label={`${label} — placeholder`}>
        <span className="shot__label">{label}</span>
        {hint ? <span>{hint}</span> : null}
    </div>
);
